import React, { useState, useEffect } from 'react';
import Wrapper from '../../Main/Universal/Wrapper';
import { postAPI, postAPIForm } from '../../../app/api';
import editIcon from "../../../theme/assets/images/icon/edit-icon.svg"
import deleteIcon from "../../../theme/assets/images/icon/delete-icon.svg"
import Swal from 'sweetalert2';

export default function Announcement() {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewAnnouncement, setViewAnnouncement] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        start_datetime: '',
        end_datetime: '',
        banner_image: null,
        status: 1
    });

    // Fetch all announcements
    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const fetchAnnouncements = async () => {
        try {
            setLoading(true);
            const response = await postAPI('announcement/list', {});
            if (response?.status === 1) {
                setAnnouncements(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching announcements:', error);
            Swal.fire('Error', 'Failed to fetch announcements', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle form changes
    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: files ? files[0] : value
        }));
    };

    // Add/Update announcement
    const handleAddAnnouncement = async (e) => {
        e.preventDefault();
        try {
            if (!formData.title || !formData.content || !formData.start_datetime) {
                Swal.fire('Validation', 'Please fill all required fields', 'warning');
                return;
            }

            let submitData = {
                title: formData.title,
                content: formData.content,
                start_datetime: formData.start_datetime,
                end_datetime: formData.end_datetime,
                status: formData.status
            };

            if (editingId) {
                submitData.id = editingId;
            }

            // If banner image exists, upload it
            if (formData.banner_image && typeof formData.banner_image !== 'string') {
                const fileFormData = new FormData();
                fileFormData.append('baseDir', 'announcements');
                fileFormData.append('filename', 'announcement_banner');
                fileFormData.append('original_file', formData.banner_image);
                
                const uploadResponse = await postAPIForm('file/uploadFile', fileFormData);
                if (uploadResponse?.status === 1) {
                    submitData.banner_image = uploadResponse?.filepath;
                }
            } else if (typeof formData.banner_image === 'string') {
                submitData.banner_image = formData.banner_image;
            }

            const response = await postAPI('announcement/create', submitData);
            if (response?.status === 1) {
                Swal.fire('Success', 'Announcement saved successfully', 'success');
                resetForm();
                fetchAnnouncements();
            } else {
                Swal.fire('Error', response?.msg || 'Failed to save announcement', 'error');
            }
        } catch (error) {
            console.error('Error saving announcement:', error);
            Swal.fire('Error', error.message || 'Failed to save announcement', 'error');
        }
    };

    // Delete announcement
    const handleDeleteAnnouncement = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: 'Are you sure you want to delete this announcement?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('announcement/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Announcement deleted successfully', 'success');
                    fetchAnnouncements();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete announcement', 'error');
            }
        }
    };

    // Toggle status
    const handleToggleStatus = async (id) => {
        try {
            const response = await postAPI('announcement/toggleStatus', { id });
            if (response?.status === 1) {
                Swal.fire('Success', 'Status updated successfully', 'success');
                fetchAnnouncements();
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to update status', 'error');
        }
    };

    // Handle view announcement
    const handleView = (announcement) => {
        setViewAnnouncement(announcement);
        setShowViewModal(true);
    };

    const resetForm = () => {
        setFormData({
            title: '',
            content: '',
            start_datetime: '',
            end_datetime: '',
            banner_image: null,
            status: 1
        });
        setShowForm(false);
        setEditingId(null);
    };

    const handleToggleForm = () => {
        if (showForm) {
            setShowForm(false);
            resetForm();
        } else {
            setEditingId(null);
            setFormData({
                title: '',
                content: '',
                start_datetime: '',
                end_datetime: '',
                banner_image: null,
                status: 1
            });
            setShowForm(true);
        }
    };

    const handleEditAnnouncement = (announcement) => {
        setFormData({
            title: announcement.title || '',
            content: announcement.content || '',
            start_datetime: announcement.start_datetime ? announcement.start_datetime.split('T')[0] + 'T' + announcement.start_datetime.split('T')[1].substring(0, 5) : '',
            end_datetime: announcement.end_datetime ? announcement.end_datetime.split('T')[0] + 'T' + announcement.end_datetime.split('T')[1].substring(0, 5) : '',
            banner_image: announcement.banner_image || null,
            status: announcement.status || 1
        });
        setEditingId(announcement.id);
        setShowForm(true);
    };

    return (
        <>
            <Wrapper>
                <div className="container-fluid">
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center">
                                <h3 className="mb-0">Announcements Management</h3>
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleToggleForm}
                                >
                                    {showForm ? 'Cancel' : 'Add New Announcement'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {showForm && (
                        <div className="card mb-4">
                            <div className="card-body">
                                <h5 className="card-title">{editingId ? 'Edit Announcement' : 'Add New Announcement'}</h5>
                                <form onSubmit={handleAddAnnouncement}>
                                    <div className="row">
                                        <div className="col-md-12 mb-3">
                                            <label className="form-label">Title *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Start Date & Time *</label>
                                            <input
                                                type="datetime-local"
                                                className="form-control"
                                                name="start_datetime"
                                                value={formData.start_datetime}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">End Date & Time</label>
                                            <input
                                                type="datetime-local"
                                                className="form-control"
                                                name="end_datetime"
                                                value={formData.end_datetime}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Banner Image</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                name="banner_image"
                                                onChange={handleChange}
                                                accept="image/*"
                                            />
                                            {formData.banner_image && typeof formData.banner_image === 'string' && (
                                                <small className="text-muted">Current: {formData.banner_image}</small>
                                            )}
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-control"
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                            >
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label className="form-label">Content *</label>
                                            <textarea
                                                className="form-control"
                                                name="content"
                                                value={formData.content}
                                                onChange={handleChange}
                                                rows="5"
                                                required
                                            ></textarea>
                                        </div>
                                    </div>
                                    <button type="submit" className="btn btn-success">
                                        {editingId ? 'Update' : 'Add'} Announcement
                                    </button>
                                </form>
                            </div>
                        </div>
                    )}

                    <div className="card">
                        <div className="card-body">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-striped table-hover cus-table">
                                        <thead className="table-dark">
                                            <tr>
                                                <th>Title</th>
                                                <th>Start Date</th>
                                                <th>End Date</th>
                                                <th>Content Preview</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {announcements && announcements.length > 0 ? (
                                                announcements.map((announcement, index) => (
                                                    <tr key={index}>
                                                        <td><strong>{announcement.title}</strong></td>
                                                        <td>{announcement.start_datetime ? new Date(announcement.start_datetime).toLocaleString() : '-'}</td>
                                                        <td>{announcement.end_datetime ? new Date(announcement.end_datetime).toLocaleString() : '-'}</td>
                                                        <td>{announcement.content?.substring(0, 50)}...</td>
                                                        <td>
                                                            <button
                                                                className={`btn btn-sm badge-${announcement.status === 1 ? 'success' : 'danger'}`}
                                                                onClick={() => handleToggleStatus(announcement.id)}
                                                                style={{ cursor: 'pointer', border: 'none' }}
                                                            >
                                                                {announcement.status === 1 ? 'Active' : 'Inactive'}
                                                            </button>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-primary me-2 px-1 py-1 my-0 mx-0"
                                                                onClick={() => handleView(announcement)}
                                                                title="View Details"
                                                            >
                                                                <i className={'fa fa-eye'} alt="View Details" />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm me-0 py-0 my-0 mx-0"
                                                                onClick={() => handleEditAnnouncement(announcement)}
                                                            >
                                                                <img src={editIcon} alt="Edit" />
                                                            </button>
                                                            <button
                                                                className="btn py-0 my-0 mx-0"
                                                                onClick={() => handleDeleteAnnouncement(announcement.id)}
                                                            >
                                                                <img src={deleteIcon} alt="Delete" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-4">
                                                        No announcements found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* View Announcement Details Modal */}
                <div className={`modal fade ${showViewModal ? "show d-block modal-white" : ""}`} tabIndex="-1" style={{ background: showViewModal ? "rgba(0,0,0,0.5)" : "transparent" }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Announcement Details</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowViewModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {viewAnnouncement && (
                                    <div className="row">
                                        <div className="col-12 mb-3">
                                            {viewAnnouncement.banner_image && (
                                                <div className="text-center mb-3">
                                                    <img 
                                                        src={`${process.env.REACT_APP_API_URL || ''}/uploads/announcements/${viewAnnouncement.banner_image}`} 
                                                        alt={viewAnnouncement.title}
                                                        style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'cover' }}
                                                        onError={(e) => { e.target.style.display = 'none' }}
                                                    />
                                                </div>
                                            )}
                                            <table className="table table-bordered">
                                                <tbody>
                                                    <tr>
                                                        <th style={{ width: '25%' }}>Title</th>
                                                        <td><strong>{viewAnnouncement.title}</strong></td>
                                                    </tr>
                                                    <tr>
                                                        <th>Start Date & Time</th>
                                                        <td>{viewAnnouncement.start_datetime ? new Date(viewAnnouncement.start_datetime).toLocaleString() : '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>End Date & Time</th>
                                                        <td>{viewAnnouncement.end_datetime ? new Date(viewAnnouncement.end_datetime).toLocaleString() : '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Content</th>
                                                        <td style={{ whiteSpace: 'pre-wrap' }}>{viewAnnouncement.content}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>Status</th>
                                                        <td>
                                                            <span className={`badge badge-${viewAnnouncement.status === 1 ? 'success' : 'danger'}`}>
                                                                {viewAnnouncement.status === 1 ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowViewModal(false)}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => {
                                        setShowViewModal(false);
                                        handleEditAnnouncement(viewAnnouncement);
                                    }}
                                >
                                    Edit Announcement
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Wrapper>
        </>
    );
}

